import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import useAuthStore from '../stores/useAuthStore';
import { useToast } from '../components/ToastContainer';
import axiosClient from '../services/api';
import useCourseStore from '../stores/useCourseStore';

const InstructorCouponPage = () => {
    const { user } = useAuthStore();
    const toast = useToast();
    const { courses, fetchCourses } = useCourseStore();
    const [coupons, setCoupons] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentCoupon, setCurrentCoupon] = useState(null);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        code: '',
        discountType: 'percentage',
        discountValue: '',
        courses: [],
        maxUses: '',
        expiresAt: ''
    });

    const [myCourses, setMyCourses] = useState([]);

    const fetchCoupons = async () => {
        setIsLoading(true);
        try {
            const res = await axiosClient.get('/coupons');
            setCoupons(res.data.coupons.filter(c => c.creatorRole === "instructor" && c.createdBy
                === user._id));
        } catch (err) {
            toast.error('Failed to load coupons');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (courses) {
            setMyCourses(courses.filter(c => c.instructor?._id === user._id))
        }
    }, [courses]);

    useEffect(() => {
        fetchCoupons();
        fetchCourses();
    }, []);

    const resetForm = () => {
        setFormData({
            code: '',
            discountType: 'percentage',
            discountValue: '',
            courses: [],
            maxUses: '',
            expiresAt: ''
        });
        setCurrentCoupon(null);
        setIsEditing(false);
    };

    const openCreateModal = () => {
        resetForm();
        setIsModalOpen(true);
    };

    const openEditModal = (coupon) => {
        const courseIds = coupon.courses.map(c => c._id);

        setFormData({
            code: coupon.code,
            discountType: coupon.discountType,
            discountValue: coupon.discountValue,
            courses: courseIds,
            maxUses: coupon.maxUses || '',
            expiresAt: coupon.expiresAt ? new Date(coupon.expiresAt).toISOString().split('T')[0] : ''
        });
        setCurrentCoupon(coupon);
        setIsEditing(true);
        setIsModalOpen(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleCourseToggle = (courseId) => {
        setFormData(prev => {
            const courses = [...prev.courses];
            if (courses.includes(courseId)) {
                return { ...prev, courses: courses.filter(id => id !== courseId) };
            } else {
                return { ...prev, courses: [...courses, courseId] };
            }
        });
    };

    const handleSubmit = async (e) => {
        setIsSubmitting(true);
        e.preventDefault();
        try {
            const payload = {
                code: formData.code,
                discountType: formData.discountType,
                discountValue: parseFloat(formData.discountValue),
                appliesTo: 'specific_courses',
                courses: formData.courses,
                maxUses: formData.maxUses ? parseInt(formData.maxUses) : null,
                expiresAt: formData.expiresAt || null
            };

            if (isEditing && currentCoupon) {
                await axiosClient.put(`/coupons/${currentCoupon._id}`, {
                    ...payload,
                    courseIds: payload.courses,
                });

                toast.success('Coupon updated!');
            } else {
                await axiosClient.post('/coupons', {
                    ...payload,
                    courseIds: payload.courses,
                });

                toast.success('Coupon created!');
            }

            setIsModalOpen(false);
            fetchCoupons();
        } catch (err) {
            const msg = err.response?.data?.error || 'Operation failed';
            toast.error(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this coupon?')) return;
        try {
            await axiosClient.delete(`/api/coupons/${id}`);
            toast.success('Coupon deleted');
            fetchCoupons();
        } catch (err) {
            toast.error('Failed to delete');
        }
    };

    return (
        <DashboardLayout userRole="instructor">
            <div className="py-8">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="mb-8 flex justify-between items-center">
                        <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">My Coupons</h1>
                        <button
                            onClick={openCreateModal}
                            className="px-4 py-2 bg-primary-500 text-white rounded hover:bg-primary-600"
                        >
                            Create Coupon
                        </button>
                    </div>

                    {isLoading ? (
                        <div className="flex justify-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                        </div>
                    ) : coupons.length === 0 ? (
                        <div className="text-center py-12">
                            <div className="mr-4 mt-1 mb-8">
                                <span className="text-6xl">🎟️</span>
                            </div>
                            <p className="text-neutral-600 dark:text-neutral-400">You haven’t created any coupons yet.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {coupons.map(coupon => (
                                <div key={coupon._id} className="bg-white border border-neutral-200 dark:border-neutral-700 dark:bg-neutral-800 rounded-lg shadow p-6">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <span className="inline-block px-3 py-1 bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300 rounded-full text-sm font-medium">
                                                {coupon.code}
                                            </span>
                                            <p className="mt-2 text-lg font-semibold dark:text-neutral-300">
                                                {coupon.discountType === 'percentage'
                                                    ? `${coupon.discountValue}% OFF`
                                                    : `₦${Number(coupon.discountValue).toLocaleString('en-NG', { minimumFractionDigits: 2 })} OFF`}
                                            </p>
                                            <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                                                {coupon.courses.length} course{coupon.courses.length !== 1 ? 's' : ''}
                                            </p>
                                        </div>
                                        <div className="flex space-x-2">
                                            <button
                                                onClick={() => openEditModal(coupon)}
                                                className="text-sm text-primary-600 hover:text-primary-800 dark:text-primary-400"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDelete(coupon._id)}
                                                className="text-sm text-danger-600 hover:text-danger-800 dark:text-danger-400"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                    <div className="mt-4 text-sm text-neutral-600 dark:text-neutral-400">
                                        <div>Used: {coupon.usedCount} / {coupon.maxUses || '∞'}</div>
                                        {coupon.expiresAt && (
                                            <div>Expires: {new Date(coupon.expiresAt).toLocaleDateString()}</div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Modal */}
                {isModalOpen && (
                    <div className="fixed inset-0 z-50 overflow-y-auto">
                        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block">
                            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setIsModalOpen(false)}></div>
                            <div className="inline-block align-bottom bg-white dark:bg-neutral-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                                <div className="bg-white dark:bg-neutral-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                    <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-4">
                                        {isEditing ? 'Edit Coupon' : 'Create Coupon for Your Courses'}
                                    </h3>
                                    <form onSubmit={handleSubmit} className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Coupon Code</label>
                                            <input
                                                type="text"
                                                name="code"
                                                value={formData.code}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                required
                                                disabled={isEditing}
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Type</label>
                                                <select
                                                    name="discountType"
                                                    value={formData.discountType}
                                                    onChange={handleInputChange}
                                                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                    required
                                                >
                                                    <option value="percentage">Percentage</option>
                                                    <option value="fixed">Fixed Amount</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                                    {formData.discountType === 'percentage' ? 'Percent (%)' : 'Amount (₦)'}
                                                </label>
                                                <input
                                                    type="number"
                                                    name="discountValue"
                                                    value={formData.discountValue}
                                                    onChange={handleInputChange}
                                                    min="0"
                                                    step="0.1"
                                                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Select Courses</label>
                                            <div className="max-h-40 overflow-y-auto border border-neutral-200 dark:border-neutral-700 rounded p-2">
                                                {myCourses.map(course => (
                                                    <div key={course._id} className="flex items-center mb-1">
                                                        <input
                                                            type="checkbox"
                                                            id={`mycourse-${course._id}`}
                                                            checked={formData.courses.includes(course._id)}
                                                            onChange={() => handleCourseToggle(course._id)}
                                                            className="mr-2"
                                                        />
                                                        <label htmlFor={`mycourse-${course._id}`} className="text-sm dark:text-neutral-300">{course.title} (₦{Number(course.price).toLocaleString('en-NG', { minimumFractionDigits: 2 })})</label>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Max Uses</label>
                                                <input
                                                    type="number"
                                                    name="maxUses"
                                                    value={formData.maxUses}
                                                    onChange={handleInputChange}
                                                    min="1"
                                                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Expiry</label>
                                                <input
                                                    type="date"
                                                    name="expiresAt"
                                                    value={formData.expiresAt}
                                                    onChange={handleInputChange}
                                                    className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                />
                                            </div>
                                        </div>
                                        <div className="flex justify-end space-x-3 pt-4">
                                            <button
                                                type="button"
                                                onClick={() => setIsModalOpen(false)}
                                                className="px-4 py-2 text-neutral-700 dark:text-neutral-300 rounded"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={isSubmitting}
                                                className={`px-4 py-2 bg-primary-500 text-white rounded hover:bg-primary-600 flex items-center justify-center ${isSubmitting && 'opacity-70 cursor-not-allowed'}`}
                                            >
                                                {isSubmitting ? (
                                                    <div className="flex items-center">
                                                        <div className="animate-spin h-4 w-4 border-b-2 border-white mr-2"></div>
                                                        {isEditing ? 'Updating...' : 'Creating...'}
                                                    </div>
                                                ) : (
                                                    isEditing ? 'Update' : 'Create'
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

export default InstructorCouponPage;